import * as React from "react";
import { Box, Container, Drawer, IconButton, Typography, useScrollTrigger } from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import { Link, NavLink, useLocation } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { LanguageSwitcher } from "./LanguageSwitcher";
import { PrimaryButton } from "../ui/primitives";
import { colors, company } from "../ui/tokens";
import { findProject } from "../data/projects";

// A case study highlights Products or Work depending on the kind of project.
const LINKS = [
  { to: "/products", key: "products", match: ["/products"], kind: (k) => k === "product" },
  { to: "/ourwork", key: "work", match: ["/ourwork"], kind: (k) => k !== "product" },
  { to: "/services", key: "services", match: ["/services"] },
  { to: "/aboutus", key: "about", match: ["/aboutus"] },
  { to: "/contact", key: "contact", match: ["/contact"] },
];

function AppNavigationBar() {
  const { t } = useTranslation('Common');
  const { pathname } = useLocation();
  const [open, setOpen] = React.useState(false);
  const scrolled = useScrollTrigger({ disableHysteresis: true, threshold: 8 });

  React.useEffect(() => setOpen(false), [pathname]);

  const shown = pathname.startsWith("/project/") ? findProject(pathname.split("/")[2]) : null;
  const isActive = (link) => link.match.some((m) => pathname.startsWith(m)) || Boolean(shown && link.kind?.(shown.kind));

  return (
    <Box
      component="header"
      sx={{
        position: "sticky",
        top: 0,
        zIndex: 1100,
        backgroundColor: "rgba(255,255,255,0.92)",
        backdropFilter: "saturate(180%) blur(12px)",
        borderBottom: `1px solid ${scrolled ? colors.line : "transparent"}`,
        transition: "border-color .2s",
      }}
    >
      <Container maxWidth="lg">
        <Box sx={{ height: { xs: 64, md: 76 }, display: "flex", alignItems: "center", gap: 3 }}>
          <Link to="/" aria-label={company.shortName} style={{ display: "flex", alignItems: "center" }}>
            <Box component="img" src="/assets/oxide/oxide-logo-dark.svg" alt={company.legalName} sx={{ height: { xs: 38, md: 44 }, width: "auto" }} />
          </Link>

          <Box component="nav" aria-label="Main" sx={{ display: { xs: "none", md: "flex" }, gap: 0.5, ml: "auto" }}>
            {LINKS.map((link) => (
              <Box
                key={link.to}
                component={NavLink}
                to={link.to}
                sx={{
                  position: "relative",
                  px: { md: 1.25, lg: 1.75 },
                  py: 1,
                  fontSize: { md: "0.92rem", lg: "0.95rem" },
                  whiteSpace: "nowrap",
                  fontWeight: 500,
                  textDecoration: "none",
                  color: isActive(link) ? colors.ink : colors.body,
                  "&:hover": { color: colors.ink },
                  "&::after": {
                    content: '""',
                    position: "absolute",
                    left: { md: 10, lg: 14 },
                    right: { md: 10, lg: 14 },
                    bottom: 2,
                    height: "2px",
                    backgroundColor: colors.orange,
                    transform: isActive(link) ? "scaleX(1)" : "scaleX(0)",
                    transformOrigin: "left",
                    transition: "transform .2s",
                  },
                }}
              >
                {t(`nav.${link.key}`)}
              </Box>
            ))}
          </Box>

          <Box sx={{ display: { xs: "none", md: "flex" }, alignItems: "center", gap: 2 }}>
            <LanguageSwitcher />
            <PrimaryButton to="/contact" sx={{ py: 1, px: 2.25, fontSize: "0.92rem", whiteSpace: "nowrap", display: { md: "none", lg: "inline-flex" } }}>
              {t('nav.cta')}
            </PrimaryButton>
          </Box>

          <IconButton
            onClick={() => setOpen(true)}
            aria-label={t('nav.menu')}
            sx={{ display: { xs: "inline-flex", md: "none" }, ml: "auto", color: colors.ink }}
          >
            <MenuIcon />
          </IconButton>
        </Box>
      </Container>

      <Drawer
        anchor="right"
        open={open}
        onClose={() => setOpen(false)}
        PaperProps={{ sx: { width: "100%", maxWidth: 420, backgroundColor: colors.navy, color: colors.onDark } }}
      >
        <Box sx={{ px: 3, height: 64, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box component="img" src="/assets/oxide/oxide-logo-light.svg" alt={company.legalName} sx={{ height: 38, width: "auto" }} />
          <IconButton onClick={() => setOpen(false)} aria-label={t('nav.close')} sx={{ color: colors.onDark }}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Box component="nav" aria-label="Main" sx={{ px: 3, pt: 4, display: "flex", flexDirection: "column" }}>
          {LINKS.map((link) => (
            <Typography
              key={link.to}
              component={Link}
              to={link.to}
              sx={{
                py: 2,
                fontSize: "1.6rem",
                fontWeight: 700,
                letterSpacing: "-0.01em",
                textDecoration: "none",
                color: isActive(link) ? colors.orange : colors.onDark,
                borderBottom: `1px solid ${colors.lineDark}`,
              }}
            >
              {t(`nav.${link.key}`)}
            </Typography>
          ))}
          <Box sx={{ mt: 4, display: "flex", flexDirection: "column", gap: 3, alignItems: "flex-start" }}>
            <PrimaryButton to="/contact" dark>{t('nav.cta')}</PrimaryButton>
            <LanguageSwitcher dark />
            <Box sx={{ color: colors.onDarkMuted, fontSize: "0.95rem", lineHeight: 1.8 }}>
              <Box component="a" href={`mailto:${company.email}`} sx={{ color: "inherit", display: "block" }}>{company.email}</Box>
              <Box component="a" href={company.phoneHref} sx={{ color: "inherit", display: "block" }}>{company.phone}</Box>
            </Box>
          </Box>
        </Box>
      </Drawer>
    </Box>
  );
}

export default AppNavigationBar;
